/**
 * MassnahmenCardContent Component
 * Customer Journey card with phase chevron arrows, descriptions,
 * and top 3 measures per funnel step.
 * Desktop: horizontal layout. Mobile: vertical stacked layout.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FUNNEL_CATEGORIES } from '@/types/massnahmen';
import type { MassnahmenFullData, FunnelCategory, MassnahmeTableEntry } from '@/types/massnahmen';

export interface MassnahmenCardContentProps {
  data: MassnahmenFullData;
}

const JOURNEY_COLORS: Record<FunnelCategory, string> = {
  Awareness: '#d91680',
  Consideration: '#e84470',
  Conversion: '#f5693d',
  Loyalty: '#ff8c00',
};

function getCategorizedTable(
  table: Record<FunnelCategory, MassnahmeTableEntry[]> | MassnahmeTableEntry[] | undefined,
): Record<FunnelCategory, MassnahmeTableEntry[]> {
  if (!table) return { Awareness: [], Consideration: [], Conversion: [], Loyalty: [] };
  if (Array.isArray(table)) return { Awareness: table, Consideration: [], Conversion: [], Loyalty: [] };
  return table;
}

export function MassnahmenCardContent({ data }: MassnahmenCardContentProps) {
  const { t } = useTranslation();
  const { main } = data;
  const categorizedTable = getCategorizedTable(main.Massnahmen_Tabelle);

  return (
    <div className="flex flex-col h-full space-y-3 pt-2">
      {/* ── Desktop Layout ─────────────────────────────── */}
      <div className="hidden md:flex md:flex-col md:space-y-3">
        {/* Phase chevron arrows with connectors */}
        <div className="flex items-center gap-0.5">
          {FUNNEL_CATEGORIES.map((cat, i) => {
            const nextCat = FUNNEL_CATEGORIES[i + 1];
            return (
              <Fragment key={cat}>
                <div className="flex-1 min-w-0">
                  <div
                    className="py-2.5 text-center"
                    style={{
                      backgroundColor: JOURNEY_COLORS[cat],
                      clipPath:
                        'polygon(0% 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 0% 100%)',
                    }}
                  >
                    <span className="text-[11px] font-bold text-white tracking-wide whitespace-nowrap pr-3">
                      {t(`results.cards.massnahmen.card.phaseNames.${cat.toLowerCase()}`)}
                    </span>
                  </div>
                </div>
                {nextCat && (
                  <div className="flex items-center flex-shrink-0 -mx-0.5">
                    <div
                      className="w-3.5 h-[2px]"
                      style={{
                        background: `linear-gradient(90deg, ${JOURNEY_COLORS[cat]}, ${JOURNEY_COLORS[nextCat]})`,
                      }}
                    />
                    <div
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                        borderLeft: `5px solid ${JOURNEY_COLORS[nextCat]}`,
                        opacity: 0.6,
                      }}
                    />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>

        {/* Phase descriptions — below chevrons */}
        <div className="grid grid-cols-4 gap-2">
          {FUNNEL_CATEGORIES.map(cat => (
            <p
              key={cat}
              className="text-[11px] text-[var(--color-charcoal)]/50 leading-snug text-center px-1"
            >
              {t(`results.cards.massnahmen.card.phaseDescriptions.${cat.toLowerCase()}`)}
            </p>
          ))}
        </div>

        {/* Top 3 measures per phase */}
        <div className="grid grid-cols-4 gap-2 pt-1">
          {FUNNEL_CATEGORIES.map(cat => {
            const items = categorizedTable[cat] || [];
            const color = JOURNEY_COLORS[cat];
            return (
              <div key={cat} className="space-y-1.5 p-2.5 rounded-lg bg-gray-50/60">
                <p className="text-[11px] font-semibold text-[var(--color-charcoal)]/60 leading-snug">
                  {t(`results.cards.massnahmen.card.top3Headers.${cat.toLowerCase()}`)}
                </p>
                {items.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 min-w-0">
                    {m.Rang_in_Phase != null ? (
                      <span
                        className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        {m.Rang_in_Phase}
                      </span>
                    ) : (
                      <span
                        className="w-[7px] h-[7px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: color, opacity: 0.7 }}
                      />
                    )}
                    <span className="text-[12px] text-[var(--color-charcoal)]/70 truncate leading-snug">
                      {m.Titel}
                    </span>
                  </div>
                ))}
                {items.length === 0 && (
                  <span className="text-[11px] text-[var(--color-charcoal)]/30 italic">&mdash;</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Mobile Layout ──────────────────────────────── */}
      <div className="flex flex-col space-y-3 md:hidden">
        {FUNNEL_CATEGORIES.map((cat, i) => {
          const items = categorizedTable[cat] || [];
          const color = JOURNEY_COLORS[cat];
          return (
            <Fragment key={cat}>
              {i > 0 && <div className="border-t border-gray-100/80" />}
              <div className="flex flex-col gap-1.5">
                {/* Phase chevron with description inside */}
                <div
                  className="py-2.5 px-3 w-full"
                  style={{
                    backgroundColor: JOURNEY_COLORS[cat],
                    clipPath:
                      'polygon(0% 0%, calc(100% - 16px) 0%, 100% 50%, calc(100% - 16px) 100%, 0% 100%)',
                  }}
                >
                  <span className="text-[11px] font-bold text-white leading-none whitespace-nowrap pr-5">
                    {i + 1}. {t(`results.cards.massnahmen.card.phaseNames.${cat.toLowerCase()}`)}
                  </span>
                  <p className="text-[10px] text-white/70 leading-snug mt-0.5 pr-5">
                    {t(`results.cards.massnahmen.card.phaseDescriptions.${cat.toLowerCase()}`)}
                  </p>
                </div>

                {/* Top 3 measures */}
                <div className="space-y-1 p-2 rounded-lg bg-gray-50/60">
                  <p className="text-[10px] font-semibold text-[var(--color-charcoal)]/55 leading-snug">
                    {t(`results.cards.massnahmen.card.top3Headers.${cat.toLowerCase()}`)}
                  </p>
                  {items.map((m, j) => (
                    <div key={j} className="flex items-center gap-1.5 min-w-0">
                      {m.Rang_in_Phase != null ? (
                        <span
                          className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: color }}
                        >
                          {m.Rang_in_Phase}
                        </span>
                      ) : (
                        <span
                          className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                          style={{ backgroundColor: color, opacity: 0.7 }}
                        />
                      )}
                      <span className="text-[11px] text-[var(--color-charcoal)]/65 truncate leading-snug">
                        {m.Titel}
                      </span>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <span className="text-[10px] text-[var(--color-charcoal)]/30 italic">&mdash;</span>
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default MassnahmenCardContent;
